import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Statistic,
  Tag,
  Timeline,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import axios from "axios";
import SimpleTable from "../components/simpleTable";
import { AppContext } from "../context/AppContext";

const { Text } = Typography;

const DeliveriesPage = () => {
  const { company } = useContext(AppContext);
  const token = localStorage.getItem("token");

  const [deliveries, setDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryForm] = Form.useForm();

  const [borrowerOptions, setBorrowerOptions] = useState([]);
  const [borrowerFetching, setBorrowerFetching] = useState(false);
  const [selectedBorrower, setSelectedBorrower] = useState(null);

  const [riders, setRiders] = useState([]);
  const [loadingRiders, setLoadingRiders] = useState(false);
  const [assigningDelivery, setAssigningDelivery] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [assignForm] = Form.useForm();

  const filteredDeliveries = useMemo(() => {
    if (filterType === "all") return deliveries;
    return deliveries.filter((delivery) => delivery.status === filterType);
  }, [deliveries, filterType]);

  const statusConfig = {
    delivered: { color: "green", icon: <CheckCircleOutlined /> },
    pending: { color: "gold", icon: <ClockCircleOutlined /> },
    in_transit: { color: "blue", icon: <EnvironmentOutlined /> },
    cancelled: { color: "red", icon: <CloseCircleOutlined /> },
  };

  const getRecipientName = (delivery) => {
    if (delivery.customer_name) return delivery.customer_name;
    if (delivery.recipient) {
      const firstName = delivery.recipient.first_name;
      const lastName = delivery.recipient.last_name;
      if (firstName && lastName) return `${firstName} ${lastName}`;
      if (firstName) return firstName;
      if (lastName) return lastName;
    }
    return "Unknown";
  };

  const getRecipientPhone = (delivery) => {
    if (delivery.phone_number) return delivery.phone_number;
    return delivery.recipient?.phone || "N/A";
  };

  const getRecipientAddress = (delivery) => {
    return delivery.delivery_address || delivery.recipient?.address || "N/A";
  };

  const getAssignedRiderName = (delivery) => {
    if (!delivery.driver_id) return "Unassigned";
    const firstName = delivery.driver_id.first_name;
    const lastName = delivery.driver_id.last_name;
    if (firstName && lastName) return `${firstName} ${lastName}`;
    return firstName || lastName || "Assigned";
  };

  const fetchDeliveries = async () => {
    setLoadingDeliveries(true);
    try {
      const response = await axios.get(
        "https://zedlenders.pickmesms.com/items/deliveries",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: {
            fields:
              "*,recipient.id,recipient.first_name,recipient.last_name,recipient.phone,recipient.address,driver_id.id,driver_id.first_name,driver_id.last_name",
            ...(company?.id ? { "filter[company]": company.id } : {}),
          },
        }
      );
      setDeliveries(response.data.data || []);
    } catch (err) {
      console.error("Error fetching deliveries:", err);
      message.error("Failed to load deliveries");
    } finally {
      setLoadingDeliveries(false);
    }
  };

  const fetchRiders = async () => {
    if (riders.length > 0) return;
    setLoadingRiders(true);
    try {
      const response = await axios.get(
        "https://zedlenders.pickmesms.com/users",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: {
            fields: "id,first_name,last_name,email,phone",
            "filter[role][name][_eq]": "Rider",
            ...(company?.id ? { "filter[company]": company.id } : {}),
            limit: -1,
          },
        }
      );
      setRiders(response.data.data || []);
    } catch (err) {
      console.error("Error fetching riders:", err);
      message.error("Failed to load riders");
    } finally {
      setLoadingRiders(false);
    }
  };

  const searchBorrowers = async (searchText) => {
    if (!searchText) {
      setBorrowerOptions([]);
      return;
    }
    setBorrowerFetching(true);
    try {
      const response = await axios.get("https://zedlenders.pickmesms.com/users", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        params: {
          fields: "id,first_name,last_name,phone,address,email",
          "filter[role][name][_eq]": "Borrower",
          ...(company?.id ? { "filter[company]": company.id } : {}),
          search: searchText,
          limit: 20,
        },
      });
      setBorrowerOptions(response.data.data || []);
    } catch (err) {
      console.error("Error searching borrowers:", err);
      message.error("Failed to load borrowers");
    } finally {
      setBorrowerFetching(false);
    }
  };

  const handleBorrowerSelect = (borrowerId) => {
    const borrower =
      borrowerOptions.find((item) => item.id === borrowerId) || null;
    setSelectedBorrower(borrower);
    if (borrower) {
      const fullName = `${borrower.first_name || ""} ${borrower.last_name || ""}`.trim();
      deliveryForm.setFieldsValue({
        customer_name: fullName || borrower.email || "",
        phone_number: borrower.phone || "",
        delivery_address: borrower.address || "",
      });
    }
  };

  const handleAddDelivery = async (values) => {
    try {
      const payload = {
        ...values,
        recipient: values.recipient,
        tracking_number: `TRK${Date.now()}`,
      };

      if (values.status === "delivered" && !values.actual_delivery_date) {
        payload.actual_delivery_date = new Date().toISOString().split("T")[0];
      }

      const response = await axios.post(
        "https://zedlenders.pickmesms.com/items/deliveries",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data) {
        message.success("Delivery scheduled successfully");
        setShowDeliveryModal(false);
        deliveryForm.resetFields();
        setSelectedBorrower(null);
        setBorrowerOptions([]);
        fetchDeliveries();
      }
    } catch (err) {
      console.error("Error scheduling delivery:", err);
      message.error("Failed to schedule delivery");
    }
  };

  const openAssignModal = async (delivery) => {
    setSelectedDelivery(delivery);
    setAssignModalOpen(true);
    assignForm.resetFields();
    await fetchRiders();
  };

  const handleAssignRider = async (values) => {
    if (!selectedDelivery?.id) return;
    setAssigningDelivery(true);
    try {
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/deliveries/${selectedDelivery.id}`,
        { driver_id: values.driver_id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      message.success("Rider assigned successfully");
      setAssignModalOpen(false);
      setSelectedDelivery(null);
      assignForm.resetFields();
      fetchDeliveries();
    } catch (err) {
      console.error("Error assigning rider:", err);
      message.error("Failed to assign rider");
    } finally {
      setAssigningDelivery(false);
    }
  };

  useEffect(() => {
    fetchDeliveries();
  }, [company?.id]);

  return (
    <div className="content-wrapper">
      <Card
        title={
          <Space>
            <Badge color="#1890ff" />
            <span>Deliveries</span>
          </Space>
        }
        extra={
          <Space>
            <Button type="primary" onClick={() => setShowDeliveryModal(true)}>
              New Delivery
            </Button>
            <Segmented
              options={["All", "Pending", "In Transit", "Delivered"]}
              defaultValue="All"
              onChange={(value) => {
                const map = {
                  All: "all",
                  Pending: "pending",
                  "In Transit": "in_transit",
                  Delivered: "delivered",
                };
                setFilterType(map[value] || "all");
              }}
            />
          </Space>
        }
      >
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} md={8}>
            <Statistic
              title="Total Deliveries"
              value={deliveries.length}
              prefix={<EnvironmentOutlined />}
            />
          </Col>
          <Col xs={24} md={8}>
            <Statistic
              title="Pending Deliveries"
              value={deliveries.filter((d) => d.status === "pending").length}
              valueStyle={{ color: "#faad14" }}
            />
          </Col>
          <Col xs={24} md={8}>
            <Statistic
              title="Delivered"
              value={deliveries.filter((d) => d.status === "delivered").length}
              valueStyle={{ color: "#52c41a" }}
            />
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Card title="Delivery Timeline" bordered={false}>
              <Timeline mode="alternate">
                {loadingDeliveries ? (
                  <Spin />
                ) : (
                  filteredDeliveries.map((delivery) => {
                    const config = statusConfig[delivery.status] || {
                      color: "default",
                      icon: <ClockCircleOutlined />,
                    };
                    return (
                      <Timeline.Item
                        key={delivery.id}
                        color={config.color}
                        label={
                          delivery.scheduled_date
                            ? new Date(
                                delivery.scheduled_date
                              ).toLocaleDateString()
                            : "No date"
                        }
                        dot={config.icon}
                      >
                        {delivery.commodity || "No commodity"}
                        <Tag color={config.color} style={{ marginLeft: 8 }}>
                          {delivery.status?.toUpperCase() || "UNKNOWN"}
                        </Tag>
                        <br />
                        <Text type="secondary">
                          {getRecipientName(delivery)} -{" "}
                          {getRecipientAddress(delivery)}
                        </Text>
                        <br />
                        <Text type="secondary">
                          Tracking: {delivery.tracking_number || "N/A"}
                        </Text>
                      </Timeline.Item>
                    );
                  })
                )}
              </Timeline>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <SimpleTable
              loading={loadingDeliveries}
              data={filteredDeliveries}
              columns={[
                {
                  title: "Scheduled Date",
                  dataIndex: "scheduled_date",
                  render: (date) =>
                    date ? new Date(date).toLocaleDateString() : "N/A",
                  sorter: (a, b) =>
                    new Date(a.scheduled_date || 0) -
                    new Date(b.scheduled_date || 0),
                },
                {
                  title: "Customer",
                  dataIndex: "customer_name",
                  render: (_, record) => getRecipientName(record),
                },
                {
                  title: "Phone",
                  dataIndex: "phone_number",
                  render: (_, record) => getRecipientPhone(record),
                },
                {
                  title: "Address",
                  dataIndex: "delivery_address",
                  render: (_, record) => getRecipientAddress(record),
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  render: (status) => {
                    const color = statusConfig[status]?.color || "default";
                    return (
                      <Tag color={color}>
                        {status?.toUpperCase() || "UNKNOWN"}
                      </Tag>
                    );
                  },
                  filters: [
                    { text: "Delivered", value: "delivered" },
                    { text: "Pending", value: "pending" },
                    { text: "In Transit", value: "in_transit" },
                    { text: "Cancelled", value: "cancelled" },
                  ],
                  onFilter: (value, record) => record.status === value,
                },
                {
                  title: "Rider",
                  dataIndex: "driver_id",
                  render: (_, record) => (
                    <Tag color={record.driver_id ? "blue" : "default"}>
                      {getAssignedRiderName(record)}
                    </Tag>
                  ),
                },
                {
                  title: "Actions",
                  key: "actions",
                  render: (_, record) => (
                    <Button
                      size="small"
                      icon={<UserSwitchOutlined />}
                      onClick={() => openAssignModal(record)}
                    >
                      Assign Rider
                    </Button>
                  ),
                },
              ]}
              pagination={{ pageSize: 6 }}
              rowKey="id"
            />
          </Col>
        </Row>
      </Card>

      <Modal
        title="Schedule New Delivery"
        visible={showDeliveryModal}
        onCancel={() => {
          setShowDeliveryModal(false);
          deliveryForm.resetFields();
          setSelectedBorrower(null);
          setBorrowerOptions([]);
        }}
        footer={null}
      >
        <Form form={deliveryForm} layout="vertical" onFinish={handleAddDelivery}>
          <Form.Item
            name="recipient"
            label="Borrower"
            rules={[{ required: true, message: "Please select a borrower" }]}
          >
            <Select
              showSearch
              allowClear
              placeholder="Search borrower by name, phone, or NRC"
              onSearch={searchBorrowers}
              filterOption={false}
              loading={borrowerFetching}
              notFoundContent={borrowerFetching ? <Spin size="small" /> : null}
              onChange={(value) => {
                if (!value) {
                  setSelectedBorrower(null);
                  deliveryForm.setFieldsValue({
                    customer_name: "",
                    phone_number: "",
                    delivery_address: "",
                  });
                  return;
                }
                handleBorrowerSelect(value);
              }}
              options={borrowerOptions.map((borrower) => ({
                value: borrower.id,
                label: `${borrower.first_name || ""} ${borrower.last_name || ""}`.trim() ||
                  borrower.email,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="customer_name"
            label="Customer Name"
            rules={[{ required: true, message: "Please enter customer name" }]}
          >
            <Input placeholder="Enter customer name" />
          </Form.Item>

          <Form.Item
            name="phone_number"
            label="Phone Number"
            rules={[{ required: true, message: "Please enter phone number" }]}
          >
            <Input placeholder="Enter phone number" />
          </Form.Item>

          <Form.Item
            name="delivery_address"
            label="Delivery Address"
            rules={[
              { required: true, message: "Please enter delivery address" },
            ]}
          >
            <Input placeholder="Enter delivery address" />
          </Form.Item>

          <Form.Item
            name="scheduled_date"
            label="Scheduled Date"
            rules={[
              { required: true, message: "Please select a scheduled date" },
            ]}
          >
            <Input type="date" min={new Date().toISOString().split("T")[0]} />
          </Form.Item>

          <Form.Item
            name="commodity"
            label="Commodity"
            rules={[{ required: true, message: "Please enter commodity" }]}
          >
            <Select placeholder="Select commodity">
              <Select.Option value="Sugar">Sugar</Select.Option>
              <Select.Option value="Mealie Meal">Mealie Meal</Select.Option>
              <Select.Option value="Fertilizer">Fertilizer</Select.Option>
              <Select.Option value="Rice">Rice</Select.Option>
              <Select.Option value="Assorted Groceries">
                Assorted Groceries
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            initialValue="pending"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="in_transit">In Transit</Select.Option>
              <Select.Option value="delivered">Delivered</Select.Option>
              <Select.Option value="cancelled">Cancelled</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button
              style={{ marginRight: 8 }}
              onClick={() => {
                setShowDeliveryModal(false);
                deliveryForm.resetFields();
                setSelectedBorrower(null);
                setBorrowerOptions([]);
              }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Schedule Delivery
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Assign Rider"
        visible={assignModalOpen}
        onCancel={() => {
          setAssignModalOpen(false);
          setSelectedDelivery(null);
          assignForm.resetFields();
        }}
        footer={null}
      >
        <Spin spinning={loadingRiders}>
          <Form form={assignForm} layout="vertical" onFinish={handleAssignRider}>
            <Form.Item label="Delivery">
              <Text>
                {selectedDelivery ? getRecipientName(selectedDelivery) : "-"}
              </Text>
              <div>
                <Text type="secondary">
                  {selectedDelivery
                    ? getRecipientAddress(selectedDelivery)
                    : ""}
                </Text>
              </div>
            </Form.Item>
            <Form.Item
              name="driver_id"
              label="Select Rider"
              rules={[{ required: true, message: "Please select a rider" }]}
            >
              <Select
                placeholder="Choose rider"
                options={riders.map((rider) => ({
                  value: rider.id,
                  label: `${rider.first_name || ""} ${rider.last_name || ""}`.trim() ||
                    rider.email,
                }))}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
              <Button
                style={{ marginRight: 8 }}
                onClick={() => {
                  setAssignModalOpen(false);
                  setSelectedDelivery(null);
                  assignForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={assigningDelivery}>
                Assign Rider
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </div>
  );
};

export default DeliveriesPage;
